import React, { forwardRef } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Table Root ────────────────────────────────────────────── */
export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  hoverable?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, striped = false, hoverable = false, children, ...props }, ref) => (
    <div className="w-full overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table
        ref={ref}
        data-striped={striped || undefined}
        data-hoverable={hoverable || undefined}
        className={cn('w-full border-collapse text-sm', className)}
        {...props}
      >
        {children}
      </table>
    </div>
  )
);

/* ─── TableHeader ───────────────────────────────────────────── */
export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-gray-50 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
      className
    )}
    {...props}
  >
    {children}
  </thead>
));

/* ─── TableBody ─────────────────────────────────────────────── */
export const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      '[&[data-striped]>tr:nth-child(even)]:bg-gray-50 dark:[&[data-striped]>tr:nth-child(even)]:bg-gray-800/30',
      className
    )}
    {...props}
  >
    {children}
  </tbody>
));

/* ─── TableFooter ───────────────────────────────────────────── */
export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-gray-200 bg-gray-50 font-medium dark:border-gray-700 dark:bg-gray-800/60',
      className
    )}
    {...props}
  >
    {children}
  </tfoot>
));

/* ─── TableRow ──────────────────────────────────────────────── */
export const TableRow = forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, children, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-gray-200 transition-colors dark:border-gray-700',
      'data-[hoverable]:hover:bg-gray-50 dark:data-[hoverable]:hover:bg-gray-800/40',
      className
    )}
    {...props}
  >
    {children}
  </tr>
));

/* ─── TableHead (sortable header cell) ──────────────────────── */
export type SortDirection = 'asc' | 'desc' | null;

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sortable, sortDirection, onSort, children, ...props }, ref) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        'px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider',
        sortable && 'cursor-pointer select-none',
        className
      )}
      aria-sort={
        sortDirection === 'asc'
          ? 'ascending'
          : sortDirection === 'desc'
            ? 'descending'
            : sortable
              ? 'none'
              : undefined
      }
      tabIndex={sortable ? 0 : undefined}
      role={sortable ? 'columnheader' : undefined}
      onClick={sortable ? onSort : undefined}
      onKeyDown={sortable ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort?.();
        }
      } : undefined}
      {...props}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && (
          <span className="inline-flex flex-col" aria-hidden="true">
            <ArrowUp
              className={cn(
                'h-3 w-3 -mb-0.5',
                sortDirection === 'asc' ? 'text-primary-600' : 'text-gray-300 dark:text-gray-600'
              )}
            />
            <ArrowDown
              className={cn(
                'h-3 w-3 -mt-0.5',
                sortDirection === 'desc' ? 'text-primary-600' : 'text-gray-300 dark:text-gray-600'
              )}
            />
          </span>
        )}
      </span>
    </th>
  )
);

/* ─── TableCell ─────────────────────────────────────────────── */
export const TableCell = forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, children, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'px-4 py-3 text-gray-700 dark:text-gray-300',
      className
    )}
    {...props}
  >
    {children}
  </td>
));

Table.displayName = 'Table';
TableHeader.displayName = 'TableHeader';
TableBody.displayName = 'TableBody';
TableFooter.displayName = 'TableFooter';
TableRow.displayName = 'TableRow';
TableHead.displayName = 'TableHead';
TableCell.displayName = 'TableCell';
